import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Alert,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleIcon from '@mui/icons-material/Article';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { useAuditDetailController } from './context/audit-detail-controller';
import { useAuditDetailState } from './context/audit-detail-context';
import { getAuditStatusLabel, getAuditStatusColor, handleViewDocument, handleDownloadDocument } from 'utils';

export function AuditDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail } = useAuditDetailController();
  const { audit, loading, error } = useAuditDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(parseInt(id));
    }
  }, [id]);

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={2} />;
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  if (!audit) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 2 }}>
            Auditoria não encontrada.
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={`Auditoria - ${audit.processNumber}`}
          subtitle={audit.unitName || 'Detalhes da auditoria'}
          breadcrumbs={[
            { label: 'Auditorias', href: '/auditorias' },
            { label: audit.processNumber },
          ]}
        />

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ArticleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                {audit.processNumber}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={audit.statusDescription || getAuditStatusLabel(audit.status)}
                  color={getAuditStatusColor(audit.status)}
                  sx={{ mr: 1 }}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            {audit.description && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                  <DescriptionIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Descrição
                    </Typography>
                    <Typography variant="body1">{audit.description}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Unidade
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {audit.unitName || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ano
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {audit.year || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Documento */}
        {audit.url && (
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionIcon sx={{ mr: 1 }} />
              Documento da Auditoria
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ArticleIcon sx={{ fontSize: 36, color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    Relatório de Auditoria
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Processo: {audit.processNumber}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Visualizar" arrow>
                  <IconButton
                    color="primary"
                    onClick={() => handleViewDocument(audit.url)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                      },
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Baixar" arrow>
                  <IconButton
                    color="success"
                    onClick={() => handleDownloadDocument(audit.url)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'success.main',
                        color: 'white',
                      },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Botão de voltar - centralizado no final */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/auditorias')}
            size="large"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Voltar para Auditorias
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
}
